/**
 * LazyShell — wraps a lazy-loaded module with both ErrorBoundary and
 * Suspense. SSOT for the "async route with fallback + crash
 * resilience" pattern.
 *
 * Loading fallback hierarchy:
 *   1. Explicit `loadingFallback` prop — wins if provided.
 *   2. `routeKey` prop → dispatched via <PageSkeleton> so the Suspense
 *      fallback mirrors the destination page's layout (Tier-1 content
 *      skeleton — dashboard widgets, list cards, chat thread, etc.).
 *   3. Fallback: generic <PageSkeleton route="generic" />.
 *
 * No more plain "Loading…" text — every lazy chunk boot shows a
 * destination-shaped skeleton so the navigation reads as "page arriving"
 * rather than a blank wait.
 */

import { Suspense, type ReactNode } from 'react';
import { ErrorBoundary } from './ErrorBoundary';
import { PageSkeleton, type PageSkeletonRoute } from '../ui/skeleton';

type Props = {
    /** Short label used in the crash fallback + telemetry. */
    scope: string;
    children: ReactNode;
    /** Route identifier used to pick a destination-shaped skeleton. */
    routeKey?: PageSkeletonRoute;
    /** Escape hatch — bypass PageSkeleton entirely. */
    loadingFallback?: ReactNode;
};

export function LazyShell({ scope, children, routeKey, loadingFallback }: Props) {
    const fallback = loadingFallback ?? <PageSkeleton route={routeKey ?? 'generic'} />;
    return (
        <ErrorBoundary scope={scope}>
            <Suspense fallback={fallback}>
                {children}
            </Suspense>
        </ErrorBoundary>
    );
}
